import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/button';
import { Label } from '@atlaskit/form/label/default';
import { ErrorMessage } from '@atlaskit/form/error-message';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Textfield from '@atlaskit/textfield/text-field';

import { SidePanelBody, SidePanelFooter } from '@confluence/side-panel';

import type { ContentDetailsById, RawAction } from '../__types__/SpaceSuggestion.ts';
import { resolveContentName } from '../utils/buildContentDetails.ts';

export type SuggestionActionEditorProps = {
	/** The existing action to edit. Currently only 'RENAME' is supported. */
	action: Extract<RawAction, { actionType: 'RENAME' }>;
	/** Called with the updated action when the form is saved with valid input. */
	onSave: (action: RawAction) => void;
	/** Returns to the review list without saving. */
	onBack: () => void;
	/** Resolved content details keyed by content id, used to render the current name. */
	contentDetails: ContentDetailsById;
};

export function SuggestionActionEditor({
	action,
	onSave,
	onBack,
	contentDetails,
}: SuggestionActionEditorProps): React.JSX.Element {
	const { formatMessage } = useIntl();

	const { title: initialTitle } = action.parameters;
	const displayName = resolveContentName(action.parameters.contentId, contentDetails);

	const [title, setTitle] = useState(initialTitle);
	const [submitted, setSubmitted] = useState(false);

	const handleTitleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setTitle(event.currentTarget.value);
	}, []);

	const handleRevert = useCallback(() => {
		setTitle(initialTitle);
	}, [initialTitle]);

	const isModified = title !== initialTitle;
	const isTitleMissing = useMemo(() => !title.trim(), [title]);

	const handleSubmit = useCallback(() => {
		setSubmitted(true);
		if (isTitleMissing) return;

		onSave({
			actionStepId: action.actionStepId,
			parentActionStepId: action.parentActionStepId,
			actionType: 'RENAME',
			parameters: {
				...action.parameters,
				title: title.trim(),
			},
		});
	}, [action, isTitleMissing, onSave, title]);

	const titleFieldId = 'suggestion-action-editor-title';
	const isInvalid = submitted && isTitleMissing;

	return (
		<>
			<SidePanelBody>
				<Stack space="space.200">
					<Stack space="space.050">
						<Text weight="medium">{formatMessage(i18n.currentNameLabel)}</Text>
						<Text>{displayName}</Text>
					</Stack>
					<Stack space="space.050">
						<Label htmlFor={titleFieldId}>{formatMessage(i18n.newTitleLabel)}</Label>
						<Flex direction="row" gap="space.100" alignItems="center">
							<Textfield
								id={titleFieldId}
								value={title}
								onChange={handleTitleChange}
								isInvalid={isInvalid}
							/>
							{isModified && (
								<Button appearance="subtle" onClick={handleRevert}>
									{formatMessage(i18n.revert)}
								</Button>
							)}
						</Flex>
						{isInvalid && <ErrorMessage>{formatMessage(i18n.requiredError)}</ErrorMessage>}
					</Stack>
				</Stack>
			</SidePanelBody>

			<SidePanelFooter>
				<Flex direction="row" gap="space.100" justifyContent="end">
					<Button onClick={onBack}>{formatMessage(i18n.back)}</Button>
					<Button appearance="primary" onClick={handleSubmit}>
						{formatMessage(i18n.save)}
					</Button>
				</Flex>
			</SidePanelFooter>
		</>
	);
}

const i18n = defineMessages({
	currentNameLabel: {
		id: 'space-suggestions.action-editor.current-name-label',
		defaultMessage: 'Current name',
		description: 'Label above the read-only current file name in the edit-action form',
	},
	newTitleLabel: {
		id: 'space-suggestions.action-editor.new-title-label',
		defaultMessage: 'New title',
		description: 'Label for the editable new title field in the edit-action form (sentence case)',
	},
	requiredError: {
		id: 'space-suggestions.action-editor.required-error',
		defaultMessage: 'This field is required',
		description:
			'Validation error shown under the title field when left empty in the edit-action form',
	},
	back: {
		id: 'space-suggestions.action-editor.back-button',
		defaultMessage: 'Back',
		description: 'Label for the button that returns to the review list without saving',
	},
	save: {
		id: 'space-suggestions.action-editor.save-button',
		defaultMessage: 'Save',
		description: 'Label for the button that saves the edited action',
	},
	revert: {
		id: 'space-suggestions.action-editor.revert-button',
		defaultMessage: 'Undo',
		description:
			'Label for the button that resets the title field back to the originally suggested value',
	},
});
